import React, { useState, useEffect, useCallback } from "react";
import LoadingInComponent from "../Components/LoadingInComponent";
import Button from "../Components/Button";
import WidgetCard from "../Components/WidgetCard";
import axios from "axios";
import {
  EXPORT_GUEST_REPORT_API,
  GET_GUEST_REPORT_API,
  GET_REPORT_WIDGET_API,
} from "../Constant/api";
import { useToast } from "../Components/Toast";
import ReportTable from "../Components/Tables/ReportTable";
import fileDownload from "js-file-download";
import Breadcrumb from "../Components/Breadcrumb";
import Can from "../Components/Can";
import { statusOptions } from "../Constant/options";

const ReportOverview = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchCleared, setIsSearchCleared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [widgetData, setWidgetData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [tableMeta, setTableMeta] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [perPage, setPerPage] = useState(10);

  const { toast } = useToast();

  const handlePageClick = (e) => {
    fetchGuestData(e.selected + 1);
  };

  const handlePerPageChange = (e) => {
    setPerPage(e.target.value);
    fetchGuestData(1, e.target.value);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchGuestData();
    }
  };

  const clearFilter = () => {
    setSearchTerm("");
    setIsSearchCleared(true);
  };

  const fetchWidgetData = useCallback(async () => {
    setLoading(true);
    await axios
      .get(GET_REPORT_WIDGET_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((response) => {
        setLoading(false);
        setWidgetData(response.data.data);
      })
      .catch((error) => {
        setLoading(false);
        toast("Failed to fetch widget data", "error");
      });
  }, [toast]);

  const fetchGuestData = useCallback(
    async (page = 1, perPageValue = perPage) => {
      setLoading(true);
      await axios
        .get(GET_GUEST_REPORT_API, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: {
            eventId: sessionStorage.getItem("event_id"),
            page: page,
            search: searchTerm,
            perPage: perPageValue,
            status: selectedStatus,
          },
        })
        .then((response) => {
          setLoading(false);
          setTableData(response.data.data);
          setTableMeta(response.data.meta.pagination);
        })
        .catch((error) => {
          setLoading(false);
          toast("Failed to fetch table data", "error");
        });
    },
    [perPage, searchTerm, selectedStatus, toast]
  );

  const exportGuestReport = async () => {
    try {
      axios
        .post(
          EXPORT_GUEST_REPORT_API,
          { eventId: sessionStorage.getItem("event_id") },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
            },
            responseType: "blob",
          }
        )
        .then((res) => {
          fileDownload(res.data, "guest_report.xlsx");
        })
        .catch((err) => {
          toast("Failed to fetch guest list template.", "error");
          console.log(err);
        });
    } catch (error) {
      toast("Failed to fetch guest list template.", "error");
      console.log(error);
    }
  };

  const handleChangeStatus = (statusId) => {
    setSelectedStatus(statusId);
  };

  useEffect(() => {
    fetchWidgetData();
    fetchGuestData();
  }, [selectedStatus, fetchWidgetData, fetchGuestData]);

  useEffect(() => {
    if (isSearchCleared && searchTerm === "") {
      fetchGuestData();
      setIsSearchCleared(false);
    }
  }, [isSearchCleared, searchTerm, fetchGuestData]);

  const breadcrumbItems = [{ label: "Report Overview" }, { label: "List" }];

  return (
    <div className="flex flex-col w-full h-full">
      <LoadingInComponent isLoading={loading}>
        {!loading && (
          <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] no-scrollbar overflow-y-auto gap-[12px] md:gap-[24px]">
            {/* Breadcrumb */}
            <Breadcrumb items={breadcrumbItems} />

            {/* Title row */}
            <div className="flex w-full justify-between items-center">
              <h1 className="text-[29px]">Report Overview</h1>
              <Can permission="export_report">
                <Button variant="primary" onClick={exportGuestReport}>
                  Export Report
                </Button>
              </Can>
            </div>

            {/* Widgets */}
            {widgetData && (
              <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-[12px] md:gap-[24px]">
                <WidgetCard
                  name="Attended Guests"
                  data={widgetData.attended_guests}
                />
                <WidgetCard
                  name="Not Attending Guests"
                  data={widgetData.not_attended_guests}
                />
              </div>
            )}

            {/* Table */}
            <ReportTable
              data={tableData}
              meta={tableMeta}
              handlePageClick={handlePageClick}
              handleSearch={handleSearch}
              handleKeyDown={handleKeyDown}
              clearFilter={clearFilter}
              searchTerm={searchTerm}
              statusOptions={statusOptions}
              selectedStatus={selectedStatus}
              handleChangeStatus={handleChangeStatus}
              perPage={perPage}
              handlePerPageChange={handlePerPageChange}
            />
          </div>
        )}
      </LoadingInComponent>
    </div>
  );
};

export default ReportOverview;
